import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/ui/LanguageToggle';

/**
 * Splash landing page — first impression with animated SWG logo.
 * Click "ENTRER" to navigate to the main site.
 */
const SplashPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleEnter = () => {
    navigate('/home');
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden bg-[#0e1112]"
      data-testid="splash-page"
    >
      {/* Language toggle, top-right */}
      <div className="absolute top-6 right-6 z-20">
        <LanguageToggle />
      </div>
      {/* Animated ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, #5B7A8A 0%, transparent 70%)' }}
          animate={{ x: [0, 80, 0], y: [0, 60, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full opacity-15 blur-3xl"
          style={{ background: 'radial-gradient(circle, #E07B54 0%, transparent 70%)' }}
          animate={{ x: [0, -60, 0], y: [0, -40, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-[800px] h-[800px] rounded-full opacity-10 blur-3xl -translate-x-1/2 -translate-y-1/2"
          style={{ background: 'radial-gradient(circle, #4A90D9 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative z-10 flex flex-col items-center px-6 text-center">
        {/* Animated logo with rotating colored sunburst icon */}
        <motion.div
          className="relative mb-10"
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: mounted ? 1 : 0.6, opacity: mounted ? 1 : 0 }}
          transition={{ duration: 1, ease: [0.25, 1, 0.5, 1] }}
          data-testid="splash-logo"
        >
          {/* Glow */}
          <motion.div
            className="absolute inset-0 rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(91,122,138,0.5) 0%, transparent 70%)' }}
            animate={{ opacity: [0.4, 0.8, 0.4], scale: [1, 1.1, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Rotating sunburst icon */}
          <motion.img
            src="/logos/swg-icon.png"
            alt="Smartworld Group"
            className="relative h-40 md:h-56 w-auto object-contain drop-shadow-[0_0_40px_rgba(91,122,138,0.5)]"
            animate={{ rotate: 360 }}
            transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
          />
        </motion.div>

        {/* SWG wordmark */}
        <motion.h1
          className="text-6xl md:text-8xl font-black tracking-tight text-white leading-none"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: mounted ? 0 : 30, opacity: mounted ? 1 : 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 1, 0.5, 1] }}
        >
          SWG
        </motion.h1>

        <motion.p
          className="mt-3 text-sm md:text-base tracking-[0.4em] uppercase text-[#9AACB8] font-medium"
          initial={{ opacity: 0 }}
          animate={{ opacity: mounted ? 1 : 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          Smartworld Group
        </motion.p>

        <motion.div
          className="mt-2 h-px w-24 bg-gradient-to-r from-transparent via-[#5B7A8A] to-transparent"
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: mounted ? 96 : 0, opacity: mounted ? 1 : 0 }}
          transition={{ duration: 1, delay: 0.9 }}
        />

        <motion.p
          className="mt-6 max-w-xl text-base md:text-lg text-[#9AACB8]"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: mounted ? 0 : 20, opacity: mounted ? 1 : 0 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          {t('splash.tagline')}
          <br />
          <span className="text-[#5B7A8A]">{t('splash.sub')}</span>
        </motion.p>

        {/* Enter button */}
        <motion.button
          onClick={handleEnter}
          className="group mt-12 inline-flex items-center gap-3 px-10 py-4 rounded-full bg-[#5B7A8A] text-white font-semibold tracking-wide uppercase text-sm hover:bg-[#6d8fa1] transition-all duration-300 shadow-[0_8px_30px_rgba(91,122,138,0.4)] hover:shadow-[0_12px_40px_rgba(91,122,138,0.6)] hover:scale-[1.03]"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: mounted ? 0 : 30, opacity: mounted ? 1 : 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          data-testid="splash-enter-button"
        >
          {t('cta.enter')}
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </motion.button>

        <motion.p
          className="mt-8 text-xs text-[#888680] tracking-widest uppercase"
          initial={{ opacity: 0 }}
          animate={{ opacity: mounted ? 0.7 : 0 }}
          transition={{ duration: 1, delay: 1.5 }}
        >
          {t('splash.hint')}
        </motion.p>
      </div>
    </div>
  );
};

export default SplashPage;
